import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, StatusBadge } from "@/components/dashboard/shared";
import { getAPIBaseURL, getAdminHeaders } from "@/lib/auth";

type Total = { currency: string; status: string; count: number; total: number };
type Overview = { deposits: Total[]; plus: Total[]; withdrawals: Total[]; bonus: Total[]; refundsPending: number };

type TxType = "deposit" | "plus" | "withdrawal" | "bonus";
type Transaction = { id: string; type: TxType; userId: string; name: string; email: string; amount: number; currency: string; status: string; detail: string | null; createdAt: string };

type RefundRequest = {
  id: string;
  status: string;
  createdAt: string;
  rejectReason: string | null;
  cardAmount: number;
  currency: string;
  note: string | null;
  heldAmount: number;
  requester: { id: string; name: string; phone: string | null };
};

const TYPE_LABELS: Record<"all" | TxType, string> = {
  all: "Tous",
  deposit: "Recharges de diamants",
  plus: "Deuxda Plus",
  withdrawal: "Retraits de chat",
  bonus: "Retraits de bonus",
};

const STATUS_OPTIONS = ["all", "completed", "pending", "awaiting_payer", "paying", "paid", "failed", "rejected", "refunded"];

const formatMoney = (amount: number, currency: string) => `${amount.toLocaleString("fr-FR")} ${currency}`;

// Sums one status-group of overview rows for one currency; used for the summary cards
function sumRows(rows: Total[] | undefined, statuses: string[]) {
  const totals: Record<string, number> = {};
  (rows ?? [])
    .filter((row) => statuses.includes(row.status))
    .forEach((row) => {
      totals[row.currency] = (totals[row.currency] ?? 0) + row.total;
    });
  const entries = Object.entries(totals);
  return entries.length ? entries.map(([currency, total]) => formatMoney(total, currency)).join(" · ") : "0";
}

export function PaymentsPage() {
  const queryClient = useQueryClient();
  const [type, setType] = React.useState<"all" | TxType>("all");
  const [status, setStatus] = React.useState("all");
  const [query, setQuery] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [rejectReasons, setRejectReasons] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    const timer = window.setTimeout(() => setSearch(query.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  const overview = useQuery({
    queryKey: ["payments-overview"],
    queryFn: async () => {
      const response = await fetch(`${getAPIBaseURL()}/api/v1/admin/payments/overview`, { headers: getAdminHeaders() });
      if (!response.ok) throw new Error("Failed to load payments");
      return (await response.json()) as Overview;
    },
  });

  const transactions = useQuery({
    queryKey: ["payments-transactions", type, status, search],
    queryFn: async () => {
      const params = new URLSearchParams({ type, status, q: search, limit: "100" });
      const response = await fetch(`${getAPIBaseURL()}/api/v1/admin/payments/transactions?${params}`, { headers: getAdminHeaders() });
      if (!response.ok) throw new Error("Failed to load transactions");
      return ((await response.json()) as { transactions: Transaction[] }).transactions;
    },
  });

  const refunds = useQuery({
    queryKey: ["payments-refunds"],
    queryFn: async () => {
      const response = await fetch(`${getAPIBaseURL()}/api/v1/admin/refunds?status=pending_review`, { headers: getAdminHeaders() });
      if (!response.ok) throw new Error("Failed to load refund requests");
      return ((await response.json()) as { requests: RefundRequest[] }).requests;
    },
  });

  const decideRefund = useMutation({
    mutationFn: async ({ id, action, reason }: { id: string; action: "approve" | "reject"; reason?: string }) => {
      const response = await fetch(`${getAPIBaseURL()}/api/v1/admin/refunds/${id}/${action}`, {
        method: "POST",
        headers: { ...getAdminHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(action === "reject" ? { reason } : {}),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.message ?? "Action impossible");
      return action;
    },
    onSuccess: (action) => {
      toast.success(action === "approve" ? "Remboursement approuvé" : "Demande refusée");
      queryClient.invalidateQueries({ queryKey: ["payments-refunds"] });
      queryClient.invalidateQueries({ queryKey: ["payments-overview"] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const data = overview.data;

  return (
    <div className="space-y-6">
      <PageHeader title="Paiements" description="Recharges, abonnements Deuxda Plus, retraits et demandes de remboursement." />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {overview.isLoading || !data ? (
          Array.from({ length: 4 }).map((_, index) => <Skeleton key={index} className="h-28 rounded-xl" />)
        ) : (
          <>
            <SummaryCard label="Recharges réussies" value={sumRows(data.deposits, ["completed"])} note="Diamants achetés" />
            <SummaryCard label="Deuxda Plus" value={sumRows(data.plus, ["active", "completed"])} note="Abonnements payés" />
            <SummaryCard label="Retraits de chat" value={sumRows(data.withdrawals, ["paid", "completed"])} note="Versés aux utilisateurs" />
            <SummaryCard label="Remboursements à traiter" value={String(data.refundsPending)} note="En attente de décision" tone={data.refundsPending > 0 ? "warning" : "neutral"} />
          </>
        )}
      </div>

      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="flex flex-wrap items-center gap-2">
            {(Object.keys(TYPE_LABELS) as Array<"all" | TxType>).map((key) => (
              <Button key={key} size="sm" variant={type === key ? "default" : "outline"} onClick={() => setType(key)}>
                {TYPE_LABELS[key]}
              </Button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Input className="max-w-xs" placeholder="Rechercher un utilisateur ou un e-mail" value={query} onChange={(event) => setQuery(event.target.value)} />
            <select
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option === "all" ? "Tous les statuts" : option}
                </option>
              ))}
            </select>
          </div>

          {transactions.isLoading ? (
            <Skeleton className="h-48 rounded-xl" />
          ) : transactions.isError ? (
            <p className="text-sm text-destructive">Impossible de charger les transactions.</p>
          ) : !transactions.data?.length ? (
            <p className="py-10 text-center text-sm text-muted-foreground">Aucune transaction.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Utilisateur</TableHead>
                  <TableHead>Détail</TableHead>
                  <TableHead>Montant</TableHead>
                  <TableHead>Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.data.map((row) => (
                  <TableRow key={`${row.type}-${row.id}`}>
                    <TableCell className="whitespace-nowrap text-sm">{new Date(row.createdAt).toLocaleString("fr-FR")}</TableCell>
                    <TableCell className="text-sm">{TYPE_LABELS[row.type]}</TableCell>
                    <TableCell>
                      <p className="text-sm font-medium">{row.name || "—"}</p>
                      <p className="text-xs text-muted-foreground">{row.email}</p>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{row.detail ?? "—"}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm font-semibold tabular-nums">{formatMoney(row.amount, row.currency)}</TableCell>
                    <TableCell>
                      <StatusBadge value={row.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-4 p-4">
          <div>
            <h3 className="text-lg font-semibold">Demandes de remboursement</h3>
            <p className="text-sm text-muted-foreground">Approuver rembourse le payeur. Refuser ferme la demande avec le motif indiqué.</p>
          </div>
          {refunds.isLoading ? (
            <Skeleton className="h-24 rounded-xl" />
          ) : !refunds.data?.length ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Aucune demande en attente.</p>
          ) : (
            <div className="space-y-3">
              {refunds.data.map((request) => (
                <div key={request.id} className="flex flex-col gap-3 rounded-lg border p-4 md:flex-row md:items-center md:justify-between">
                  <div className="min-w-0 space-y-1">
                    <p className="text-sm font-medium">{request.requester.name || "—"} {request.requester.phone ? `· ${request.requester.phone}` : ""}</p>
                    <p className="text-sm text-muted-foreground">
                      {formatMoney(request.cardAmount, request.currency)} · {new Date(request.createdAt).toLocaleString("fr-FR")}
                    </p>
                    {request.note && <p className="text-sm text-muted-foreground">Note : {request.note}</p>}
                    <p className="text-xs text-muted-foreground">Montant payé par ce payeur : {formatMoney(request.heldAmount, request.currency)}</p>
                  </div>
                  <div className="flex flex-col gap-2 md:w-72">
                    <Button size="sm" disabled={decideRefund.isPending} onClick={() => decideRefund.mutate({ id: request.id, action: "approve" })}>
                      Approuver
                    </Button>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Motif du refus"
                        value={rejectReasons[request.id] ?? ""}
                        onChange={(event) => setRejectReasons((current) => ({ ...current, [request.id]: event.target.value }))}
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={decideRefund.isPending || !(rejectReasons[request.id] ?? "").trim()}
                        onClick={() => decideRefund.mutate({ id: request.id, action: "reject", reason: (rejectReasons[request.id] ?? "").trim() })}
                      >
                        Refuser
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SummaryCard({ label, value, note, tone = "neutral" }: { label: string; value: string; note: string; tone?: "neutral" | "warning" }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs font-semibold uppercase text-muted-foreground">{label}</p>
        <p className="mt-2 text-2xl font-bold tabular-nums">{value}</p>
        <p className={tone === "warning" ? "mt-2 text-xs text-warning-foreground" : "mt-2 text-xs text-muted-foreground"}>{note}</p>
      </CardContent>
    </Card>
  );
}
